import { AppState } from '@store/AppState';
import type { FC } from 'react';
import { useSelector } from 'react-redux';
import { DealType } from '../../../../../enums/DealType';
import { PortfolioFilterType } from '../../../../../enums/PortfolioFilterType';
import { useCheckDevice } from '../../../../../hooks/UseCheckDevice';
import { usePortfolioFilter } from '../../../../../hooks/UsePortfolioFilter';
import type { PortfolioState } from '../../../../../store/portfolio/PortfolioReducer';
import type { DealerState } from '../../../../../types/DealerState';
import { defaultRange } from '../../../../../types/PortfolioFilterResource';
import MultiSelectDropdown from '../../../../shared/filters/multi-select-dropdown/MultiSelectDropdown';
import RangeDropdown from '../../../../shared/filters/range-dropdown/RangeDropdown';
import SingleSelectDropdown from '../../../../shared/filters/single-select-dropdown/SingleSelectDropdown';

type ReplacementVehicleOptionsProps = {
  onOpen: () => void;
  onClose: () => void;
};

export const ReplacementVehicleOptions: FC<ReplacementVehicleOptionsProps> = ({
  onOpen,
  onClose,
}) => {
  const { getPortfolioView, getSelectionToggleType, setSelectedFilter } = usePortfolioFilter();
  const { dealType } = useSelector<AppState, PortfolioState>(
    (portfolioState) => portfolioState.portfolio,
  );
  const currentView = getPortfolioView(dealType);
  const { isMobileDevice } = useCheckDevice();
  const { featureToggle } = useSelector<AppState, DealerState>((state) => state.dealer);

  const isNotCash = dealType?.toLowerCase() !== DealType.Cash.toLowerCase();
  const isNotLease = dealType?.toLowerCase() !== DealType.Lease.toLowerCase();

  return (
    <>
      <SingleSelectDropdown
        placeholder='Replacement Selection'
        options={currentView.filters.replacementVehicleSelection}
        value={currentView.selectedFilters.replacementVehicleSelection}
        onChange={(value) =>
          setSelectedFilter(PortfolioFilterType.ReplacementVehicleSelection, value, dealType)
        }
        onUpdate={(value) =>
          setSelectedFilter(PortfolioFilterType.ReplacementVehicleSelection, value, dealType, false)
        }
        onOpen={onOpen}
        onClose={onClose}
        selectedLabelPosition='right'
        type='am-dropdown'
        cssClass={isMobileDevice ? 'responsive-adjust' : ''}
      />
      <MultiSelectDropdown
        placeholder='Vehicle Type'
        enableCheckAll
        values={currentView.selectedFilters.replacementVehicleTypes}
        onChange={(value) =>
          setSelectedFilter(PortfolioFilterType.ReplacementVehicleTypes, value, dealType)
        }
        onUpdate={(value) =>
          setSelectedFilter(PortfolioFilterType.ReplacementVehicleTypes, value, dealType, false)
        }
        displaySelectedLabels
        selectedLabelPosition='right'
        type='am-dropdown'
        options={currentView.filters.replacementVehicleTypes}
        onOpen={onOpen}
        onClose={onClose}
        selectionToggleType={getSelectionToggleType(
          PortfolioFilterType.ReplacementVehicleTypes,
          dealType,
          currentView?.selectedFilters.replacementVehicleTypes,
        )}
        cssClass={isMobileDevice ? 'responsive-adjust' : ''}
        dataTestId='replacement-vehicle-options-vehicle-type-dropdown'
      />
      <MultiSelectDropdown
        placeholder='Year'
        allowSearchFilter
        enableCheckAll
        values={currentView.selectedFilters.replacementVehicleYears}
        onChange={(value) =>
          setSelectedFilter(PortfolioFilterType.ReplacementVehicleYears, value, dealType)
        }
        onUpdate={(value) =>
          setSelectedFilter(PortfolioFilterType.ReplacementVehicleYears, value, dealType, false)
        }
        displaySelectedLabels
        selectedLabelPosition='right'
        type='am-dropdown'
        options={currentView.filters.replacementVehicleYears}
        onOpen={onOpen}
        onClose={onClose}
        selectionToggleType={getSelectionToggleType(
          PortfolioFilterType.ReplacementVehicleYears,
          dealType,
          currentView?.selectedFilters.replacementVehicleYears,
        )}
        cssClass={isMobileDevice ? 'responsive-adjust' : ''}
        dataTestId='replacement-vehicle-options-year-dropdown'
      />
      <MultiSelectDropdown
        placeholder='Make'
        enableCheckAll
        allowSearchFilter
        values={currentView.selectedFilters.replacementVehicleMakes}
        onChange={(value) =>
          setSelectedFilter(PortfolioFilterType.ReplacementVehicleMakes, value, dealType)
        }
        onUpdate={(value) =>
          setSelectedFilter(PortfolioFilterType.ReplacementVehicleMakes, value, dealType, false)
        }
        displaySelectedLabels
        selectedLabelPosition='right'
        type='am-dropdown'
        options={currentView.filters.replacementVehicleMakes.sort((a, b) =>
          a.label.localeCompare(b.label),
        )}
        onOpen={onOpen}
        onClose={onClose}
        selectionToggleType={getSelectionToggleType(
          PortfolioFilterType.ReplacementVehicleMakes,
          dealType,
          currentView?.selectedFilters.replacementVehicleMakes,
        )}
        cssClass={isMobileDevice ? 'responsive-adjust' : ''}
        dataTestId='replacement-vehicle-options-make-dropdown'
      />
      <MultiSelectDropdown
        placeholder='Model'
        allowSearchFilter
        enableCheckAll
        values={currentView.selectedFilters.replacementVehicleModels}
        onChange={(value) =>
          setSelectedFilter(PortfolioFilterType.ReplacementVehicleModels, value, dealType)
        }
        onUpdate={(value) =>
          setSelectedFilter(PortfolioFilterType.ReplacementVehicleModels, value, dealType, false)
        }
        displaySelectedLabels
        selectedLabelPosition='right'
        type='am-dropdown'
        options={currentView.filters.replacementVehicleModels}
        onOpen={onOpen}
        onClose={onClose}
        selectionToggleType={getSelectionToggleType(
          PortfolioFilterType.ReplacementVehicleModels,
          dealType,
          currentView?.selectedFilters.replacementVehicleModels,
        )}
        cssClass={isMobileDevice ? 'responsive-adjust' : ''}
        dataTestId='replacement-vehicle-options-model-dropdown'
      />
      <MultiSelectDropdown
        placeholder='Trim'
        enableCheckAll
        allowSearchFilter
        values={currentView.selectedFilters.replacementVehicleTrims}
        onChange={(value) =>
          setSelectedFilter(PortfolioFilterType.ReplacementVehicleTrims, value, dealType)
        }
        onUpdate={(value) =>
          setSelectedFilter(PortfolioFilterType.ReplacementVehicleTrims, value, dealType, false)
        }
        displaySelectedLabels
        selectedLabelPosition='right'
        type='am-dropdown'
        options={currentView.filters.replacementVehicleTrims}
        onOpen={onOpen}
        onClose={onClose}
        selectionToggleType={getSelectionToggleType(
          PortfolioFilterType.ReplacementVehicleTrims,
          dealType,
          currentView?.selectedFilters.replacementVehicleTrims,
        )}
        cssClass={isMobileDevice ? 'responsive-adjust' : ''}
        dataTestId='replacement-vehicle-options-trim-dropdown'
      />
      {isNotLease && (
        <RangeDropdown
          type='am-dropdown'
          placeholder='Age Days'
          selectedValue={currentView?.selectedFilters.ageDays}
          possibleRange={defaultRange}
          maxLength={8}
          onOpen={onOpen}
          onClose={onClose}
          onChange={(value) => setSelectedFilter(PortfolioFilterType.AgeDays, value, dealType)}
          cssClass={isMobileDevice ? 'responsive-adjust' : ''}
        />
      )}
      {isNotLease && (
        <RangeDropdown
          type='am-dropdown'
          placeholder='Mileage'
          selectedValue={currentView?.selectedFilters.replacementVehicleMileage}
          possibleRange={defaultRange}
          maxLength={8}
          onOpen={onOpen}
          onClose={onClose}
          onChange={(value) =>
            setSelectedFilter(PortfolioFilterType.ReplacementVehicleMileage, value, dealType)
          }
          cssClass={isMobileDevice ? 'responsive-adjust' : ''}
        />
      )}
      {isNotCash && isNotLease && (
        <RangeDropdown
          type='am-dropdown'
          placeholder='Rate %'
          selectedValue={currentView?.selectedFilters.replacementVehicleRate}
          possibleRange={defaultRange}
          inputType='float'
          zeroMaxValueAllowed
          onOpen={onOpen}
          onClose={onClose}
          onChange={(value) =>
            setSelectedFilter(PortfolioFilterType.ReplacementVehicleRate, value, dealType)
          }
          cssClass={isMobileDevice ? 'responsive-adjust' : ''}
        />
      )}
    </>
  );
};
